import { Form, Input, Transfer } from 'antd';
import {
  useCreateCompanyGroupMutation,
  useUpdateCompanyGroupMutation,
} from 'app/api';
import { useCompanySlice } from 'app/containers/Company/slice';
import {
  selectConfirmedCompanies,
  selectSelectedCompanyGroupId,
} from 'app/containers/Company/slice/selectors';
import {
  ICompanyGroup,
  ICompanyInList,
} from 'app/containers/Company/slice/types';
import { handleError } from 'app/containers/Global/slice/helpers';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

interface IProps {
  onBackToGroups: () => void;
  companyGroup: ICompanyGroup | null;
}

const CreateOrEditCompanyGroup: FC<IProps> = ({
  onBackToGroups,
  companyGroup,
}) => {
  const dispatch = useDispatch();
  const companyActions = useCompanySlice().actions;

  const { t } = useTranslation();
  const [form] = Form.useForm();
  const activeCompanyGroupId = useSelector(selectSelectedCompanyGroupId);

  const [
    createCompanyGroup,
    { isLoading: isCreateLoading },
  ] = useCreateCompanyGroupMutation();

  const [
    updateCompanyGroup,
    { isLoading: isUpdateLoading },
  ] = useUpdateCompanyGroupMutation();

  const isSubmitLoading = isCreateLoading || isUpdateLoading;

  const companies = useSelector(selectConfirmedCompanies);
  const envId = useSelector(selectSelectedEnvId) as number;

  const [targetKeys, setTargetKeys] = useState<string[]>([]);

  // Set initial edit group data
  useEffect(() => {
    if (companyGroup) {
      form.setFieldsValue({ groupName: companyGroup.name });
      const companyIds = companyGroup.companies.map(company =>
        String(company.id),
      );

      setTargetKeys(companyIds);
    }
  }, [companyGroup, form]);

  const filterOption = (inputValue: string, option: ICompanyInList) =>
    option.name.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;

  const onChange = (newTargetKeys: string[]) => {
    setTargetKeys(newTargetKeys);
  };

  const onFinish = async ({ groupName }: { groupName: string }) => {
    try {
      if (isSubmitLoading) {
        return;
      }

      const companyIds = targetKeys.map(key => Number(key));
      if (companyGroup) {
        // Edit
        await updateCompanyGroup({
          envId,
          name: groupName.trim(),
          companyIds,
          companyGroupId: companyGroup.id,
        });
        if (activeCompanyGroupId && activeCompanyGroupId === companyGroup.id) {
          // Force to refetch data
          dispatch(companyActions.setSelectedCompanyGroupId(null));
          dispatch(companyActions.setSelectedCompanyGroupId(companyGroup.id));
        }
      } else {
        // Create
        await createCompanyGroup({
          envId,
          name: groupName.trim(),
          companyIds,
        }).unwrap();
      }

      onBackToGroups();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        initialValues={{ layout: 'vertical' }}
        requiredMark={true}
        id="createCompanyGroup"
        onFinish={onFinish}
      >
        <Form.Item
          label={t('Company.GroupName', 'Group name')}
          name="groupName"
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Company.GroupName', 'Group name')} />
        </Form.Item>
      </Form>

      <Transfer
        titles={[
          t('Company.CompaniesInEnv', 'The environment companies'),
          companyGroup
            ? t('Common.CompanyGroup')
            : t('Company.NewGroup', 'New group'),
        ]}
        style={{
          marginTop: 29,
          justifyContent: 'space-between',
        }}
        dataSource={companies}
        showSearch
        filterOption={filterOption}
        targetKeys={targetKeys}
        onChange={onChange}
        rowKey={company => String(company.id)}
        render={item => item.name}
        listStyle={{
          width: 290,
          height: 388,
        }}
      />
    </>
  );
};

export default CreateOrEditCompanyGroup;
